import {
  useRacePlan,
  useWorkouts,
  usePlannedWorkouts,
} from '../hooks/useProfile';
import { useProfile } from '../hooks/useProfile';
import Loader from '../pages/Loader';
import { Upgrade } from './Upgrade';
import { RacePlan } from './RacePlan';
import { useNavigate } from '@tanstack/react-router';
import { dayjs, parsePlannedDate } from '../lib/utils';
import { CalendarPlus, Check, Goal, Sparkles } from 'lucide-react';
import { useState } from 'react';
import PageHeader from './PageHeader';

const formatPlannedIntervals = (intervals: unknown): string => {
  if (!intervals) return '';
  try {
    const parsed = JSON.parse(String(intervals));
    if (Array.isArray(parsed)) {
      return parsed
        .map((interval: any) => interval.effort || interval.notes || '')
        .filter(Boolean)
        .join(' · ');
    }
  } catch {
    // Use legacy text below.
  }
  return String(intervals);
};

export const OrphanWorkouts = ({
  workouts,
}: {
  workouts: ReturnType<typeof usePlannedWorkouts>['data'];
}) => {
  const navigate = useNavigate();
  const items = workouts || [];
  const [showPreviousWeeks, setShowPreviousWeeks] = useState(false);
  const currentWeek = dayjs().startOf('week');
  const previouslyPlanned = items.filter((workout) =>
    parsePlannedDate(workout.planned_date).isBefore(currentWeek, 'day'),
  );
  const visibleItems = showPreviousWeeks
    ? items
    : items.filter(
        (workout) =>
          !parsePlannedDate(workout.planned_date).isBefore(currentWeek, 'day'),
      );
  const workoutsByWeek = [...visibleItems]
    .sort((first, second) =>
      parsePlannedDate(first.planned_date).diff(
        parsePlannedDate(second.planned_date),
      ),
    )
    .reduce<Array<{ weekStart: string; workouts: typeof visibleItems }>>(
      (groups, workout) => {
        const weekStart = parsePlannedDate(workout.planned_date)
          .startOf('week')
          .format('YYYY-MM-DD');
        const currentGroup = groups[groups.length - 1];

        if (currentGroup?.weekStart === weekStart) {
          currentGroup.workouts.push(workout);
        } else {
          groups.push({ weekStart, workouts: [workout] });
        }

        return groups;
      },
      [],
    );

  return (
    <div className="mx-auto w-full max-w-4xl space-y-4 px-1 md:px-0">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold tracking-tight text-brand-midnight dark:text-white">
          Planned workouts
        </h1>
        <button
          type="button"
          onClick={() => navigate({ to: '/plan/workout/new' })}
          className="button-secondary px-4 py-2 text-xs"
        >
          + Create workout
        </button>
      </div>

      {previouslyPlanned.length > 0 ? (
        <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
          <span>
            {previouslyPlanned.length} planned{' '}
            {previouslyPlanned.length === 1 ? 'workout' : 'workouts'} from{' '}
            previous weeks hidden
          </span>
          <span aria-hidden="true">·</span>
          <button
            type="button"
            onClick={() => setShowPreviousWeeks((current) => !current)}
            className="font-semibold text-brand-cobalt hover:text-brand-midnight dark:hover:text-white"
          >
            {showPreviousWeeks ? 'Hide previous weeks' : 'Show previous weeks'}
          </button>
        </div>
      ) : null}

      {items.length === 0 ? (
        <div className="surface-card rounded-2xl p-7 text-center text-sm text-gray-500">
          No planned workouts yet. Create one to give your week a clear focus.
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="surface-card rounded-2xl p-7 text-center text-sm text-gray-500">
          No upcoming planned workouts.
        </div>
      ) : (
        <div className="space-y-6">
          {workoutsByWeek.map((group) => (
            <section
              key={group.weekStart}
              aria-labelledby={`workouts-${group.weekStart}`}
            >
              <div className="mb-3 flex items-center gap-3">
                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-800" />
                <h2
                  id={`workouts-${group.weekStart}`}
                  className="shrink-0 text-center"
                >
                  {dayjs(group.weekStart).isSame(currentWeek, 'day') ||
                  dayjs(group.weekStart).isSame(
                    currentWeek.add(1, 'week'),
                    'day',
                  ) ? (
                    <span className="block text-xs font-bold uppercase tracking-[0.14em] text-brand-cobalt">
                      {dayjs(group.weekStart).isSame(currentWeek, 'day')
                        ? 'This week'
                        : 'Next week'}
                    </span>
                  ) : null}
                  <span className="mt-0.5 block text-xs font-semibold text-gray-500 dark:text-gray-400">
                    {dayjs(group.weekStart).format('MMM D')} –{' '}
                    {dayjs(group.weekStart).endOf('week').format('MMM D, YYYY')}
                  </span>
                </h2>
                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-800" />
              </div>
              <ul className="space-y-3">
                {group.workouts.map((workout) => (
                  <li
                    key={workout.id}
                    onClick={() =>
                      navigate({
                        to: '/plan/workout/$id',
                        params: { id: String(workout.id) },
                      })
                    }
                    className="cursor-pointer rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-100 transition-colors hover:bg-gray-50 dark:bg-gray-900 dark:ring-gray-800 dark:hover:bg-gray-800"
                  >
                    <p className="mb-2 text-xs font-bold uppercase tracking-[0.12em] text-brand-cobalt">
                      {parsePlannedDate(workout.planned_date).format(
                        'dddd, MMMM D',
                      )}
                    </p>
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="text-base font-bold text-brand-midnight dark:text-white">
                        {(workout as { title?: string }).title || workout.type}
                      </h3>
                      <div className="flex flex-col items-end gap-2">
                        {workout.distance ? (
                          <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                            {workout.distance}
                          </span>
                        ) : null}
                        {workout.completed || workout.activity_id ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                            <Check className="h-3.5 w-3.5" aria-hidden="true" />
                            Completed
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {workout.details || workout.intention ? (
                      <p className="mt-2 line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
                        {workout.details || workout.intention}
                      </p>
                    ) : null}
                    {workout.intervals ? (
                      <p className="mt-1 line-clamp-1 text-xs text-gray-500 dark:text-gray-500">
                        {formatPlannedIntervals(workout.intervals)}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export const Plan = () => {
  const navigate = useNavigate();
  const {
    data: userWorkouts,
    isLoading: workoutLoading,
    isFetching: workoutFetching,
  } = useWorkouts();
  const {
    data: racePlan,
    isLoading: racePlanLoading,
    isFetching: racePlanFetching,
  } = useRacePlan();
  const {
    data: profile,
    isLoading: profileLoading,
    isFetching: profileFetching,
  } = useProfile();
  const {
    data: plannedWorkouts,
    isLoading: plannedWorkoutsLoading,
    isFetching: plannedWorkoutsFetching,
  } = usePlannedWorkouts({
    startDate: dayjs('1970-01-01').toDate(),
    endDate: dayjs().add(10, 'year').endOf('year').toDate(),
  });
  if (
    racePlanLoading ||
    workoutLoading ||
    profileLoading ||
    plannedWorkoutsLoading ||
    racePlanFetching ||
    workoutFetching ||
    profileFetching ||
    plannedWorkoutsFetching
  ) {
    return <Loader />;
  }

  if (racePlan) {
    return (
      <>
        <PageHeader
          eyebrow="Train with intention"
          title="Training plan"
          description="Your week-by-week path to the start line, built around your goal."
          icon={Goal}
        />
        <RacePlan
          racePlan={racePlan}
          completedWorkouts={userWorkouts?.workouts || []}
        />
      </>
    );
  }

  if (profile && !racePlan) {
    return (
      <div className="mx-auto flex min-h-[60vh] w-full max-w-2xl items-center px-1 md:px-0">
        <section className="surface-card w-full rounded-3xl p-6 sm:p-10">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-midnight text-brand-volt">
            <CalendarPlus className="h-6 w-6" aria-hidden="true" />
          </div>
          <p className="eyebrow mt-6">Your plan starts with a goal</p>
          <h1 className="mt-2 max-w-lg text-3xl font-bold leading-tight tracking-[-0.045em] text-brand-midnight dark:text-white sm:text-4xl">
            Choose the race you are training for.
          </h1>
          <p className="mt-4 max-w-xl text-base leading-7 text-brand-slate dark:text-slate-300">
            We will turn your race date, distance, and schedule into a realistic week-by-week plan. Once it is ready, this is where you will see what to do next.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => navigate({ to: '/plan/create' })}
              className="button-primary min-h-12 justify-center px-6"
            >
              <CalendarPlus className="h-4 w-4" aria-hidden="true" />
              Create my race plan
            </button>
            <button
              type="button"
              onClick={() => navigate({ to: '/plan/workout/new' })}
              className="button-secondary min-h-12 justify-center px-6"
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              Generate one workout
            </button>
          </div>
          <p className="mt-4 text-xs text-brand-slate dark:text-slate-400">
            A single workout is useful for today. A race plan is the path to race day.
          </p>
        </section>
      </div>
    );
  }

  if (profile && !racePlan && plannedWorkouts && plannedWorkouts.length > 0) {
    return (
      <>
        <div className="mx-auto w-full max-w-4xl px-1 md:px-0">
          <div className="depth-panel mb-4 flex flex-col gap-3 rounded-2xl p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-bold tracking-tight text-brand-midnight dark:text-white">
                Want a full race training plan?
              </h2>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                Create a personalized plan around your goal race.
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                navigate({
                  to: profile?.user?.isPaidSubscriber
                    ? '/plan/create'
                    : '/checkout',
                })
              }
              className="button-primary shrink-0 px-5 py-2.5 text-xs"
            >
              Create race plan →
            </button>
          </div>
        </div>
        <OrphanWorkouts workouts={plannedWorkouts} />
      </>
    );
  }

  if (
    profile &&
    !racePlan &&
    !racePlanFetching &&
    !racePlanLoading &&
    !profileFetching &&
    !profileLoading &&
    !workoutFetching &&
    !workoutLoading &&
    !plannedWorkoutsFetching &&
    !plannedWorkoutsLoading
  ) {
    return null;
  }

  return <Upgrade />;
};
